// Global keymap dispatcher for the TUI dashboard. Per
// design_global_keymap (workstream `tui`):
//
//   0           toggle Commits card
//   1-4         toggle Agents/Tracks/Ready/Log card
//   5           toggle Workspaces card (feat_card_5_workspaces)
//   6           toggle In-progress card (feat_card_6_inprogress)
//   7           toggle Blocked card (feat_card_7_blocked)
//   8           toggle Recent card
//   9           toggle Doctor card (feat_card_9_doctor)
//   g           open full DAG popup (graph mnemonic; keybind-only)
//   t           open all-tasks popup (task-list mnemonic; keybind-only)
//   Shift+0/)   open Commits popup
//   ! @ # $     open fullscreen popup for that card  (Shift+1..Shift+4
//               on US keyboards; bound by glyph because ink reports
//               the post-shift character, not the modifier).
//   Shift+8/*   open Recent popup
//   + / =       tick faster (floor 100ms); = is the unshifted alias
//   -           tick slower (ceiling 10s)
//   r / F5      refresh now (poke poll loop)
//   ?           toggle help overlay
//   q / Q       quit
//   Ctrl-C      quit (handled by ink's exitOnCtrlC)
//   c           clear footer (most-recent-yank line)
//   Tab         next workstream tab (multi-ws TUI; noop when N=1)
//   Shift-Tab   previous workstream tab
//
// This module is pure TS: takes a keystroke (input + key flags from
// ink's useInput) and returns a structured GlobalAction. The caller
// (the <App> component in app.tsx) maps actions to state mutations.

export type GlobalAction =
  | { kind: "toggleCard"; cardId: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 }
  | {
      kind: "openPopup";
      cardId: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | "dag" | "allTasks";
    }
  | { kind: "tickFaster" }
  | { kind: "tickSlower" }
  | { kind: "refreshNow" }
  | { kind: "toggleHelp" }
  | { kind: "quit" }
  | { kind: "clearFooter" }
  | { kind: "nextTab" }
  | { kind: "prevTab" }
  | { kind: "noop" };

/**
 * Subset of ink's `Key` shape we care about. Defining locally so the
 * keys module stays decoupled from ink's render-time Key type.
 */
export interface KeyFlags {
  ctrl?: boolean;
  shift?: boolean;
  meta?: boolean;
  escape?: boolean;
  return?: boolean;
  upArrow?: boolean;
  downArrow?: boolean;
  leftArrow?: boolean;
  rightArrow?: boolean;
  tab?: boolean;
  pageUp?: boolean;
  pageDown?: boolean;
  f5?: boolean;
}

/**
 * Structural subset of ink's `Key` object as delivered to useInput.
 * Kept local (instead of importing ink's type) so keys.ts remains a
 * pure keymap module with no render-time dependency.
 */
export type InkKeyLike = KeyFlags;

function keyFlagsFromInk(key: InkKeyLike): KeyFlags {
  return {
    ctrl: key.ctrl,
    shift: key.shift,
    meta: key.meta,
    escape: key.escape,
    return: key.return,
    upArrow: key.upArrow,
    downArrow: key.downArrow,
    leftArrow: key.leftArrow,
    rightArrow: key.rightArrow,
    tab: key.tab,
    pageUp: key.pageUp,
    pageDown: key.pageDown,
    f5: key.f5,
  };
}

export function dispatchGlobalKeyFromInk(input: string, key: InkKeyLike): GlobalAction {
  return dispatchGlobalKey(input, keyFlagsFromInk(key));
}

/**
 * Map a single keystroke (delivered by ink's useInput) to the global
 * dashboard action it represents. Returns `{kind:"noop"}` for any
 * unrecognised input — the caller can treat noop as "do nothing".
 *
 * Pure function: no side effects, no React, no ink imports. Trivially
 * unit-testable.
 */
export function dispatchGlobalKey(input: string, key: KeyFlags): GlobalAction {
  // Quit takes precedence over everything (including help overlay
  // suppression handled by the caller).
  if (key.ctrl && input === "c") return { kind: "quit" };
  if (input === "q" || input === "Q") return { kind: "quit" };

  // Help overlay
  if (input === "?") return { kind: "toggleHelp" };

  // Refresh-now poke
  if (input === "r" || key.f5) return { kind: "refreshNow" };

  // Footer clear (last-yank line)
  if (input === "c") return { kind: "clearFooter" };

  // Multi-workstream tab navigation. Tab cycles forward;
  // Shift-Tab cycles backward. Per feat_tui_multi_workstream
  // (workstream `tui-impl`): the dispatcher returns the action
  // unconditionally; the App suppresses it (via the popup-open
  // guard) when a popup is mounted, and degenerates to a noop
  // when only one workstream is loaded.
  if (key.tab === true) {
    return { kind: key.shift === true ? "prevTab" : "nextTab" };
  }

  // Tick rate adjust. `+` arrives as `+` (Shift+= on US); `=` is the
  // unshifted alias for users who don't bother shifting; `-` is the
  // plain minus key. Digit 0 is now a card slot (Commits), so tick
  // reset is intentionally not bound to a dashboard key.
  if (input === "+" || input === "=") return { kind: "tickFaster" };
  if (input === "-") return { kind: "tickSlower" };

  // Card toggles 0-9. Slot 0 is Commits; slot 8 is Recent. DAG stays
  // a keybind-only popup on `g` so it does not consume a card slot.
  if (input >= "0" && input <= "9") {
    const cardId = (input.charCodeAt(0) - "0".charCodeAt(0)) as
      | 0
      | 1
      | 2
      | 3
      | 4
      | 5
      | 6
      | 7
      | 8
      | 9;
    return { kind: "toggleCard", cardId };
  }

  if (input === "g") return { kind: "openPopup", cardId: "dag" };
  if (input === "t") return { kind: "openPopup", cardId: "allTasks" };

  // Popup openers !-) on US keyboards. Bound by glyph because ink
  // reports the post-shift character; key.shift is false.
  // Layout-dependent — see design_global_keymap ODDITY for non-US
  // keymaps. Shift+0 opens Commits; Shift+8 opens Recent.
  const glyphMap: Record<string, 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9> = {
    ")": 0,
    "!": 1,
    "@": 2,
    "#": 3,
    $: 4,
    "%": 5,
    "^": 6,
    "&": 7,
    "*": 8,
    "(": 9,
  };
  const popupId = glyphMap[input];
  if (popupId !== undefined) return { kind: "openPopup", cardId: popupId };

  return { kind: "noop" };
}

// ─── In-popup keymap convention ────────────────────────────────────
//
// Per design_global_keymap, every popup honours:
//   j / ↓        moveDown
//   k / ↑        moveUp
//   g            jumpTop
//   G            jumpBottom
//   Ctrl-D       pageDown (half)
//   Ctrl-U       pageUp (half)
//   PgDn / PgUp  pageDown / pageUp (full)
//   /            enterFilter
//   n            nextMatch
//   N            prevMatch
//   Esc          close (or exit drill, when in drill mode)
//   q            close (alias)
//   y            yank
//   Enter        drill into the focused row (popup decides what to render)
//   ?            toggleHelp
//
// Tick-rate keys (+/-/=), refresh (r/F5), and Ctrl-C remain live in
// popups (they're global). Card toggles (0-9) and popup openers (!-))
// are SUPPRESSED by <App>.

export type PopupAction =
  | { kind: "moveDown" }
  | { kind: "moveUp" }
  | { kind: "jumpTop" }
  | { kind: "jumpBottom" }
  | { kind: "pageDown"; half: boolean }
  | { kind: "pageUp"; half: boolean }
  | { kind: "setCursor"; index: number }
  | { kind: "filter" }
  | { kind: "nextMatch" }
  | { kind: "prevMatch" }
  | { kind: "close" }
  | { kind: "yank" }
  | { kind: "drill" }
  | { kind: "verb"; key: string }
  | { kind: "noop" };

export interface PopupActionEnvelope {
  seq: number;
  action: PopupAction;
}

/**
 * Map a keystroke inside a popup to the popup-local action it
 * represents. Per-popup verbs (letter keys not in the reserved set
 * {j k g G n N q y c r w}) bubble up as `{kind: "verb", key}` so the
 * caller can switch on the literal letter.
 */
export function dispatchPopupKeyFromInk(input: string, key: InkKeyLike): PopupAction {
  return dispatchPopupKey(input, keyFlagsFromInk(key));
}

// ─── App-level swallow rules ───────────────────────────────────────
//
// Per review_tui_help_overlay_swallows_only_some_keys: the swallow
// lists for the help-overlay branch and the popup-mode branch in
// <App>'s useInput were duplicated — same conceptual rule ("do not
// let this keystroke leak into the global dashboard keymap while a
// modal is open") expressed twice with slightly different lists,
// inviting drift. Centralised here so a future binding change has
// one place to update.
//
// Caller contract: this helper ONLY decides whether to short-
// circuit BEFORE the global-keymap dispatcher (`dispatchGlobalKey`)
// runs. The caller is still responsible for App-owned actions that
// fire BEFORE the swallow check (toggling helpOpen on close keys,
// Ctrl-C universal exit). Every popup component owns its own
// `useInput`; ink fires every mounted `useInput` per keystroke, so
// the popup's local nav handler still sees `j` / `k` / etc. while
// this helper short-circuits the global one.

/** Active <App> input mode for swallow-rule selection. */
export type InputMode = "dashboard" | "help" | "popup" | "popup-filter";

/**
 * Should this keystroke be swallowed (NOT dispatched to the global
 * dashboard keymap)?
 *
 * - `dashboard` — never swallow; the global keymap is the only
 *   keymap.
 * - `help`      — swallow everything. Help owns its local scroll
 *   keys (j/k/Ctrl-D/Ctrl-U/PgUp/PgDn) via `Help`'s own useInput;
 *   nothing else should reach the global dispatcher while the
 *   overlay is up. Caller pre-handles `Esc`/`q`/`Q`/`?` close keys.
 * - `popup`     — swallow close keys (popup owns close), card
 *   toggles (`0`-`9` + `!@#$%^&*()`), the keybind-only popup
 *   openers (`g`/`t`), and footer-clear (`c`). Tick (`+`/`-`/`=`),
 *   refresh (`r`/`F5`), help toggle (`?`), and tab nav (Tab/Shift-
 *   Tab) still bubble up so they remain global even inside a popup.
 * - `popup-filter` — swallow EVERY global key. The popup's `/`
 *   filter prompt is the active text input; tick/refresh/help/etc.
 *   would compete with the user's typing.
 *
 * Ctrl-C is NEVER swallowed regardless of mode (universal escape).
 * The caller still handles it explicitly so it remains a hard
 * `exit()` even when otherwise inside a popup.
 */
export function shouldSwallowGlobalKey(input: string, key: KeyFlags, mode: InputMode): boolean {
  if (key.ctrl === true && input === "c") return false;
  switch (mode) {
    case "dashboard":
      return false;
    case "help":
      return true;
    case "popup-filter":
      return true;
    case "popup":
      if (key.escape === true || input === "q" || input === "Q") return true;
      // Guard on length: input is "" for pure-flag keys (Tab,
      // arrows, Esc, F5). Without the length check, JS's
      // `.includes("")` returns true for the popup-opener glyph
      // string and would swallow Tab.
      if (input.length === 0) return false;
      if (input >= "0" && input <= "9") return true;
      if ("!@#$%^&*()".includes(input)) return true;
      if (input === "g" || input === "t" || input === "c") return true;
      return false;
  }
}

export function shouldSwallowGlobalKeyFromInk(
  input: string,
  key: InkKeyLike,
  mode: InputMode,
): boolean {
  return shouldSwallowGlobalKey(input, keyFlagsFromInk(key), mode);
}

export function dispatchPopupKey(input: string, key: KeyFlags): PopupAction {
  if (key.escape || input === "q" || input === "Q") return { kind: "close" };
  if (input === "j" || key.downArrow) return { kind: "moveDown" };
  if (input === "k" || key.upArrow) return { kind: "moveUp" };
  if (input === "g") return { kind: "jumpTop" };
  if (input === "G") return { kind: "jumpBottom" };
  if (key.ctrl && input === "d") return { kind: "pageDown", half: true };
  if (key.ctrl && input === "u") return { kind: "pageUp", half: true };
  if (key.pageDown) return { kind: "pageDown", half: false };
  if (key.pageUp) return { kind: "pageUp", half: false };
  if (input === "/") return { kind: "filter" };
  if (input === "n") return { kind: "nextMatch" };
  if (input === "N") return { kind: "prevMatch" };
  if (input === "y") return { kind: "yank" };
  // Enter drills into the focused row. The popup chooses what
  // "drill" renders (scrollback / sub-list / notes / no-op for
  // log). The dispatcher stays purely structural — read-only.
  if (key.return) return { kind: "drill" };
  // Per-popup verbs: any letter NOT in the reserved set above is a
  // candidate for a popup-specific verb. The caller decides whether
  // to act.
  if (/^[a-zA-Z]$/.test(input)) return { kind: "verb", key: input };
  return { kind: "noop" };
}
